// Componente que muestra la sección de productos procesados.
// Displays the processed products section.
import { useTranslation } from 'react-i18next';
import { Product } from '@shared/components/ui/Product';
import { InfoBox } from './InfoBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faArrowUpArrowDown, faCheck, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { Search } from '@shared/components/ui/Search/Search';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';

interface ProcessedProductsSectionProps {
  processedProducts: Array<{
    id: string;
    name: string;
    code: string;
    image: string;
    qty: number;
  }>;
  processedProductsQuantities: Record<string, number>;
  processedProductsTotals: Record<string, number>;
  onSort: (sortBy: string) => void;
  currentSort: string;
  status?: number; // Estado del rehusado para controlar la alerta
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
}

export const ProcessedProductsSection = ({
  processedProducts,
  processedProductsQuantities,
  processedProductsTotals,
  onSort,
  currentSort,
  status,
  searchQuery = '',
  onSearchChange,
}: ProcessedProductsSectionProps) => {
  const { t } = useTranslation('receiveModule');

  return (
    <div className="w-full flex flex-col gap-5 p-4 h-full">
      {/* Header - Productos procesados */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="text-title text-lg">{t('ui.processed_products')}</h2>
          <span className="inline-flex items-center justify-center rounded-full px-2 bg-bg-info text-info">
            {processedProducts.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Search
            dataTestId="processed-products-search"
            placeholder={t('items.search_placeholder')}
            value={searchQuery}
            onChange={(e) => onSearchChange?.(e.currentTarget.value)}
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="h-8" data-testid="processed-products-sort-button">
                <FontAwesomeIcon icon={faArrowUpArrowDown} className="w-3.5 h-3.5 text-icons" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem
                data-testid="processed-products-sort-more-qty"
                onClick={() => onSort('more_qty')}
                className={currentSort === 'more_qty' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.more_qty')}
                {currentSort === 'more_qty' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="processed-products-sort-less-qty"
                onClick={() => onSort('less_qty')}
                className={currentSort === 'less_qty' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.less_qty')}
                {currentSort === 'less_qty' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="processed-products-sort-name-az"
                onClick={() => onSort('name_az')}
                className={currentSort === 'name_az' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.name_az')}
                {currentSort === 'name_az' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="processed-products-sort-name-za"
                onClick={() => onSort('name_za')}
                className={currentSort === 'name_za' ? 'bg-card-border' : ''}
              >
                {t('ui.sort.name_za')}
                {currentSort === 'name_za' && <FontAwesomeIcon icon={faCheck} className="w-3 h-3 ml-auto" />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Contenedor principal */}
      <div className="flex-1">
        {processedProducts.filter((p) => {
          const q = searchQuery.toLowerCase().trim();
          if (!q) return true;
          return (p.name || '').toLowerCase().includes(q) || (p.code || '').toLowerCase().includes(q);
        }).length > 0 ? (
          <div className="w-full space-y-4">
            {/* Solo mostrar la alerta si el estado no es "cerrado" (0) ni "por ubicar" (2) */}
            {status !== 0 && status !== 2 && <InfoBox message={t('toasts.info.process_more')} />}
            <div className="space-y-6">
              {processedProducts
                .filter((product) => {
                  const q = searchQuery.toLowerCase().trim();
                  if (!q) return true;
                  return product.name.toLowerCase().includes(q) || product.code.toLowerCase().includes(q);
                })
                .map((product) => (
                  <div key={product.id} className="flex items-center justify-between">
                    <Product id={parseInt(product.id)} src={product.image} name={product.name} code={product.code} />
                    {(() => {
                      // Para productos registrados, usar las propiedades directas
                      const processed =
                        (product as any).processedQuantity !== undefined
                          ? (product as any).processedQuantity
                          : processedProductsQuantities[product.code] || 0;
                      const total =
                        (product as any).totalQuantity !== undefined
                          ? (product as any).totalQuantity
                          : processedProductsTotals[product.code] || 0;
                      const isComplete = processed === total && total > 0;
                      const hasExcess = processed > total && total > 0;

                      return (
                        <span
                          className={`inline-flex items-center justify-center px-3 py-1 rounded-full text-xs gap-1 text-nowrap ${
                            hasExcess
                              ? 'bg-bg-warning text-warning'
                              : isComplete
                                ? 'bg-bg-success text-success'
                                : 'bg-card-border'
                          }`}
                        >
                          {hasExcess && <FontAwesomeIcon icon={faTriangleExclamation} className="w-3 h-3" />}
                          {isComplete && !hasExcess && <FontAwesomeIcon icon={faCircleCheck} className="w-3 h-3" />}
                          <span className="whitespace-nowrap">
                            {processed} / {total}
                          </span>
                        </span>
                      );
                    })()}
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center h-full mt-4 sm:mt-0">
            <div className="text-center text-complementary-text text-sm max-w-60">
              {searchQuery && searchQuery.trim() ? t('items.search.no_results.title') : t('items.empty.description')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
